"use client";

import { c as _c } from "react/compiler-runtime";
import { useContentfulLiveUpdates } from "@contentful/live-preview/react";
import type { Entry } from "contentful";
import { renderCardSectionModuleClient, renderHeroModuleClient } from "@/app/_utils/renderModuleUtilsClient";
import type { PageModule } from "@/app/_utils/TypePageModule";
import AccordionComponent from "@/components/custom/Accordion/AccordionComponent";
import AccordionFullWidth from "@/components/custom/AccordionFullWidth/AccordionFullWidth";
import CardRow from "@/components/custom/Cards/CardRow";
import ComparisonTable from "@/components/custom/ComparisonTable/ComparisonTable";
import CtaBanner from "@/components/custom/CtaBanner/CtaBanner";
import Duplex from "@/components/custom/Duplex";
import Editorial from "@/components/custom/Editorial/Editorial";
import HeroClient from "@/components/custom/Hero/HeroClient";
import HeyflowFormClient from "@/components/custom/HeyflowForm/HeyflowFormClient";
import ImageGallery from "@/components/custom/ImageGallery/ImageGallery";
import { ModuleErrorBoundary } from "@/components/custom/ModuleErrorBoundary";
import { PageBreadcrumbClient } from "@/components/custom/PageBreadcrumbClient";
import PageHeader from "@/components/custom/PageHeader";
import ProductBuilderDraft from "@/components/custom/ProductBuilder/ProductBuilderDraft";
import ProductReviews from "@/components/custom/ProductReviews/ProductReviews";
import PromotionHighlights from "@/components/custom/PromotionHighlights/PromotionHighlights";
import { Prose } from "@/components/custom/Prose";
import RelatedProductsClient from "@/components/custom/RelatedProducts/RelatedProductsClient";
import Specs from "@/components/custom/Specs";
import ValuePropsCard from "@/components/custom/ValueProps/ValuePropsCard";
import { isEntry } from "@/lib/contentful/guards";
import type { NavItemEntry } from "@/lib/contentful/navItem";
import { getRenderableModules } from "@/lib/contentful/pageModules";
import type { PageCrumb } from "@/lib/contentful/pathIndex";
import type { PdpParams } from "@/lib/product/pdpParams";
import { PdpParamsProvider } from "@/providers/PdpParamsProvider";
import type { TypePageSkeleton } from "@/types/contentful/generated";

// biome-ignore lint/suspicious/noExplicitAny: component registry accepts varied prop types
const componentMap: Record<string, React.ComponentType<any>> = {
  cardSection: CardRow,
  comparisonTable: ComparisonTable,
  ctaBanner: CtaBanner,
  faqSection: AccordionComponent,
  accordionFullWidth: AccordionFullWidth,
  duplex: Duplex,
  hero: HeroClient,
  heyflowForm: HeyflowFormClient,
  productBuilder: ProductBuilderDraft,
  productReviews: ProductReviews,
  promotionHighlights: PromotionHighlights,
  relatedProducts: RelatedProductsClient,
  specs: Specs,
  editorial: Editorial,
  valueProps: ValuePropsCard,
  imageGallery: ImageGallery,
  prose: (props: React.ComponentProps<typeof Prose>) => <Prose {...props} className="px-6 pt-6 md:mx-auto max-w-site-content" />
};
function renderModuleFromMap(module: PageModule) {
  if (!isEntry(module)) return null;
  const Component = componentMap[module.sys.contentType.sys.id];
  if (!Component) return null;
  return <ModuleErrorBoundary key={module.sys.id}>
      <Component {...module.fields} entryId={module.sys.id} />
    </ModuleErrorBoundary>;
}
interface Props {
  initialData: Entry<TypePageSkeleton, undefined, string>;
  pdpParams: PdpParams;
  navMenu: NavItemEntry[];
  chain?: PageCrumb[];
}
export default function PageClient(t0) {
  const $ = _c(17);
  const {
    initialData,
    pdpParams,
    navMenu,
    chain
  } = t0;
  const liveData = useContentfulLiveUpdates(initialData);
  const pageHeader = liveData.fields.pageHeader;
  let t1;
  if ($[0] !== chain || $[1] !== liveData.fields.breadcrumbsStructure || $[2] !== navMenu) {
    t1 = <PageBreadcrumbClient chain={chain} breadcrumbsStructure={liveData.fields.breadcrumbsStructure} navMenu={navMenu} />;
    $[0] = chain;
    $[1] = liveData.fields.breadcrumbsStructure;
    $[2] = navMenu;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== pageHeader) {
    t2 = isEntry(pageHeader) ? pageHeader : undefined;
    $[4] = pageHeader;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <PageHeader pageHeader={t2} />;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== liveData.fields.modules) {
    t4 = getRenderableModules(liveData.fields.modules).filter(_temp).map(_temp2);
    $[8] = liveData.fields.modules;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t1 || $[11] !== t3 || $[12] !== t4) {
    t5 = <div className="flex flex-col">{t1}{t3}{t4}</div>;
    $[10] = t1;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== pdpParams || $[15] !== t5) {
    t6 = <PdpParamsProvider params={pdpParams}>{t5}</PdpParamsProvider>;
    $[14] = pdpParams;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
function _temp2(module_0) {
  return renderHeroModuleClient(module_0) ?? renderCardSectionModuleClient(module_0) ?? renderModuleFromMap(module_0);
}
function _temp(module) {
  return "fields" in module;
}